import React from "react";
import FullCalendar from "@fullcalendar/react";
import resourceTimelinePlugin from "@fullcalendar/react-scheduler/resource-timeline";
import interactionPlugin from "@fullcalendar/react/interaction";
import themePlugin from '@fullcalendar/react/themes/classic'
import _ from "lodash";
import { CalendarRef, CalendarOptions, DatesSetInfo, EventDisplayInfo, ButtonInfo, EventInput, SlotLaneInfo, TableHeaderInfo } from "@fullcalendar/react";
import moment from "moment";
import clsx from "clsx";
import '@fullcalendar/react/skeleton.css'
import '@fullcalendar/react/themes/classic/theme.css'
import '@fullcalendar/react/themes/classic/palette.css'
import "./Calendar.scss";
import { ResourceInput, ColSpec, ResourceLaneInfo, ResourceExpanderInfo} from "@fullcalendar/react-scheduler";
import { getDataProps } from "../../../shared/utilities";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import { LinearProgressIndicator } from "../../../shared/components";
import { useLazyData } from "../../../shared/hooks";
import type { SourceInfo } from "@recursive-robot/react-jsx-parser";
import { useReportValidationError } from "../../../shared/hooks/useReportValidationError";

type CalendarEvent = EventInput & {
    classNames?: string[] | string;
    backgroundColor?: string;
    textColor?: string;
};

type CalendarResource = ResourceInput & {
    eventClassNames?: string[] | string;
    eventBackgroundColor?: string;
    eventTextColor?: string;
};

type CalendarData = {
    resources: CalendarResource[];
    events: CalendarEvent[];
};

type CalendarProps = CalendarOptions & {
    name?: string;
    resources?: CalendarResource[];
    events?: CalendarEvent[];
    getData?: (range: { start: Date; end: Date }) => CalendarData | Promise<CalendarData>;
    sourceInfo?: SourceInfo;
    resourceAreaWidth?: string | number;
    resourceAreaColumns?: ColSpec[];
};

// TODO: move away from old structure in client apps to remove these...
const backwardsCompatEvents = (events: CalendarEvent[]): CalendarEvent[] => {
    return events.map(e => {
        if (e.classNames && !e.className) {
            e.className = clsx(e.classNames);
        }

        if (e.backgroundColor && !e.color) {
            e.color = e.backgroundColor;
        }

        if (e.textColor && !e.contrastColor) {
            e.contrastColor = e.textColor;
        }

        return e;
    });
};

const backwardsCompatResources = (resources: CalendarResource[]): CalendarResource[] => {
    return resources.map(r => {
        if (r.eventClassNames && !r.eventClass) {
            r.eventClass = clsx(r.eventClassNames);
        }

        if (r.eventBackgroundColor && !r.eventColor) {
            r.eventColor = r.eventBackgroundColor;
        }

        if (r.eventTextColor && !r.eventContrastColor) {
            r.eventContrastColor = r.eventTextColor;
        }

        if (r.children && r.children.length > 0) {
            r.children = backwardsCompatResources(r.children);
        }

        return r;
    });
};

const defaultClassGenerators = {
    // Slots
    slotLaneClass: (info: SlotLaneInfo) => {
        let day = moment(info.date);
        return clsx(
            "fc-timeline-slot",
            day.date() === 1 && "fc-first-day-of-month",
            day.date() === day.daysInMonth() && "fc-last-day-of-month",
        );
    },
    // Events
    eventClass: (event: EventDisplayInfo) => clsx(
        "fc-event",
        !event.options.eventOverlap && "fc-timeline-event",
        event.isStart && "fc-event-start",
        event.isEnd && "fc-event-end",
        event.isFuture && "fc-event-future",
        event.isPast && "fc-event-past",
        event.isToday && "fc-event-today",
        event.isSelected && "fc-event-selected",
        event.isDraggable && "fc-event-draggable",
        event.isDragging && "fc-event-dragging",
        event.isMirror && "fc-event-mirror",
        (event.isStartResizable || event.isEndResizable) && 'fc-event-resizable',
        event.isResizing && "fc-event-resizing"
    ),
    eventInnerClass: "fc-event-main",
    eventTitleClass: "fc-event-title",
    eventTimeClass: "fc-event-time",
    rowEventClass: "fc-h-event",
    rowEventInnerClass: (event: EventDisplayInfo) => clsx(
        "fc-timeline-event",
        event.options.eventOverlap ? "fc-timeline-overlap-enabled" : "fc-timeline-overlap-disabled",
    ),
    // Links
    moreLinkClass: "fc-more-link",
    moreLinkInnerClass: "fc-more-link-inner",
    // Buttons
    buttonClass: (button: ButtonInfo) => clsx(
        "fc-button",
        "fc-button-primary",
        button.isSelected && "fc-button-selected",
        button.isDisabled && "fc-button-disabled",
        `fc-${button.name}-button`,
    ),
    // Toolbars
    headerToolbarClass: "fc-header-toolbar",
    toolbarClass: "fc-toolbar",
    toolbarTitleClass: "fc-toolbar-title",
    // Table && Cells
    tableClass: "fc-scrollgrid",
    tableBodyClass: "fc-scrollgrid-section-body",
    dayHeaderClass: "fc-col-header-cell",
    resourceDayHeaderClass: "fc-col-header-cell",
    listDayHeaderClass: "fc-col-header-cell",
    slotHeaderClass: "fc-col-header-cell",
    slotHeaderRowClass: "fc-timeline-slots",
    tableHeaderClass: (tableHeader: TableHeaderInfo) => clsx(
        "fc-col-header-cell",
        "fc-scrollgrid-section-header",
        tableHeader.isSticky && "fc-scrollgrid-section-sticky"
    ),
    // Resources
    resourceColumnHeaderInnerClass: "fc-datagrid-cell-cushion",
    resourceGroupHeaderInnerClass: "fc-datagrid-cell-cushion",
    resourceCellClass: "fc-datagrid-cell",
    resourceCellInnerClass: "fc-datagrid-cell-cushion",
    resourceExpanderClass: (data: ResourceExpanderInfo) => clsx(
        "fc-datagrid-expander",
        data.isExpanded ? "fc-icon-minus-square" : "fc-icon-plus-square",
    ),
    resourceLaneClass: "fc-timeline-lanes",
    resourceLaneTopClass: (data: ResourceLaneInfo) => clsx(
        "fc-timeline-events",
        data.options.eventOverlap ? "fc-timeline-overlap-enabled" : "fc-timeline-overlap-disabled",
    ),
    resourceLaneBottomClass: (data: ResourceLaneInfo) => clsx(
        "fc-timeline-events",
        data.options.eventOverlap ? "fc-timeline-overlap-enabled" : "fc-timeline-overlap-disabled",
    ),
}


const Calendar = (props: CalendarProps) => {
    const {
        events,
        resources,
        eventContent: _eventContent,
        getData,
        datesSet,
        sourceInfo,
        resourceAreaWidth,
        resourceAreaColumns,
        visibleRange: _visibleRange,
        ...rest
    } = props;

    const { data: fetchedData, isLoading, load } = useLazyData<CalendarData>({ fetcher: getData, errorMessage: "Failed to load calendar data." });
    const lastRangeRef = React.useRef<{ start: number, end: number }>(null);
    const reportValidationError = useReportValidationError(sourceInfo, "Calendar");

    React.useEffect(() => {
        if (getData && (events || resources)) {
            reportValidationError("Both `events`/`resources` and `getData` were provided - `events`/`resources` will be ignored.");
        }
    }, []);

    // FullCalendar invokes `datesSet` after the initial render and whenever the visible date range changes -
    // exactly the points at which lazily-loaded data should be (re)fetched...
    const handleDatesSet = (arg: DatesSetInfo) => {
        datesSet?.(arg);

        if (!getData) return;

        // Only refetch when the visible range actually changed (re-renders caused by the fetched data
        // re-invoke `datesSet` with the same range)...
        const range = { start: arg.start.valueOf(), end: arg.end.valueOf() };
        if (lastRangeRef.current?.start === range.start && lastRangeRef.current?.end === range.end) return;
        lastRangeRef.current = range;

        load({ start: arg.start, end: arg.end });
    };

    const effectiveEvents = React.useMemo(() => {
        let providedEvents = getData ? (fetchedData?.events ?? []) : events;
        return backwardsCompatEvents(providedEvents);
    }, [getData, fetchedData, events]);
    const effectiveResources = React.useMemo(() => {
        let providedResources = getData ? (fetchedData?.resources ?? []) : resources;
        return backwardsCompatResources(providedResources);
    }, [getData, fetchedData, resources]);

    // In `getData` mode the visible range is driven by FullCalendar itself (deriving it from the fetched
    // events would shift the range and trigger an endless refetch loop)...
    const visibleRange = React.useMemo(() => {
        if (getData || _visibleRange) return _visibleRange;

        const start = moment(_.min((effectiveEvents ?? []).map(e => e.start))).startOf("month").toDate();
        const end = moment(_.max((effectiveEvents ?? []).map(e => e.end))).endOf("month").toDate();

        return { start, end };
    }, [getData, effectiveEvents, _visibleRange]);

    const calendarRef = React.useRef<CalendarRef>(null);

    const defaultEventContent = React.useCallback((arg: EventDisplayInfo) => {
        const { title } = arg.event;

        return (
            <div className="fc-event-main-frame">
                <div className="fc-event-title-container">
                    <div className="fc-event-title fc-sticky">
                        <b>{title}</b>
                    </div>
                </div>
            </div>
        );
    }, []);

    const eventContent = _eventContent ?? defaultEventContent;

    // Add data attributes to the calendar element
    React.useEffect(() => {
        Object.entries(getDataProps(props)).forEach(([key, value]) => {
            calendarRef.current?.["elRef"]?.current?.setAttribute(key, value);
        });
    }, [props]);    

    const calendar = (
        <FullCalendar
            ref={calendarRef}
            className="fc fc-theme-standard"
            schedulerLicenseKey="0867129485-fcs-1787553995"
            plugins={[themePlugin, resourceTimelinePlugin, interactionPlugin]}

            events={effectiveEvents}
            resources={effectiveResources}
            visibleRange={visibleRange}
            datesSet={handleDatesSet}

            {...defaultClassGenerators}
            height="100%"

            resourceOrder={null} // Retain original resource order - don't sort by title
            editable={true}
            scrollTimeReset={false} // Don't reset the scroll position each time the calendar input is updated
            eventMinWidth={1} // Events can be rendered arbitrarily small by default

            eventContent={eventContent}
            borderless

            // backwards compatibility v6 -> v7...
            {...(resourceAreaWidth && !rest.resourceColumnsWidth ? { resourceColumnsWidth: resourceAreaWidth } : {})}
            {...(resourceAreaColumns && !rest.resourceColumns ? { resourceColumns: resourceAreaColumns } : {})}
            {...rest}
        />
    );

    // In `getData` mode, an unobtrusive progress bar is overlaid along the top edge while a fetch is in flight.
    // The calendar itself stays mounted (unmounting would reset the view state and re-trigger `datesSet`)...
    if (!getData) return calendar;

    return (
        <div className="calendar-lazy-container">
            {isLoading && <div className="calendar-loading-indicator"><LinearProgressIndicator /></div>}
            {calendar}
        </div>
    );
};

(Calendar as DocumentedComponent).injectSourceInfo = true;
(Calendar as DocumentedComponent).metadata = {
    description: "The `Calendar` component can be used to display event data in multiple views (Month, TimeGrid, List, DayGrid, Multi-Month Stack, Multi-Month Grid, Timeline, Resource Timeline, Vertical Resource, etc.).  This component is a thin wrapper around the [FullCalendar](https://fullcalendar.io/) third-party component.  Please see that component's documentation for more information on the available options and views.",
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },
        { name: "events", type: "object", exactType: "{ id: string, resourceId: string, title: string, start: Date, end: Date, [key: string]: any }[]", description: `The events to display on the calendar.  See below for the basic structure of \`Event\`.
            
### Event Fields:

| Name | Type | Description |
|------|------|-------------|
| \`id\` | \`string\` | The unique id for this event.  Optional, but highly recommended if editing functionality is used. |
| \`resourceId\` | \`string\` | The id of the resource that this event is linked to.  Optional, but required if using the \`resourceTimeline\` view. |
| \`title\` | \`string\` | The title for this event.  Optional. |
| \`start\` | \`Date\` | The start date/time for this event. |
| \`end\` | \`Date\` | The end date/time for this event. |
| \`[key: string]\` | \`any\` | Any additional metadata can be added directly to the event object. |
`
        },
        { name: "resources", type: "object", exactType: "{ id: string, title: string, [key: string]: any }[]", description: `The resources to display on the calendar.  Depending on the desired view, this may be a required input prop.  See below for the basic structure of \`Resource\`.
            
### Resource Fields:

| Name | Type | Description |
|------|------|-------------|
| \`id\` | \`string\` | The unique id for this resource. |
| \`title\` | \`string\` | The title for this resource. |
| \`[key: string]\` | \`any\` | Any additional metadata can be added directly to the resource object. |
` },
        { name: "editable", type: "boolean", description: "If `false`, the calendar not allow events to be dragged or resized.  Optional - defaults to `true`." },
        {
            name: "getData",
            type: "function",
            exactType: "(range: { start: Date, end: Date }) => { resources: { id: string, title: string, [key: string]: any }[], events: { id: string, resourceId: string, title: string, start: Date, end: Date, [key: string]: any }[] } | Promise<{ resources: { id: string, title: string, [key: string]: any }[], events: { id: string, resourceId: string, title: string, start: Date, end: Date, [key: string]: any }[] }>",
            template: "getData={(range) => $1}",
            description: `A callback function which lazily fetches the calendar data.  Serves as an alternative to \`events\`/\`resources\` - if both are provided, \`getData\` takes precedence and \`events\`/\`resources\` are ignored.

The callback is invoked with the visible date range (\`{ start, end }\`) after the initial render, and re-invoked whenever the visible range changes (e.g. when the user navigates to a different month) - enabling true lazy loading of large event datasets.  It must return an object of shape \`{ resources, events }\` (or a \`Promise\` resolving to it).  **Note:** \`async\` arrow functions are not supported by the template parser - write a regular arrow function which *returns* a \`Promise\` instead, e.g. \`getData={(range) => fetchDatasourceByName("myDatasource", range)}\`.

An unobtrusive progress bar is displayed along the top edge of the calendar while a fetch is in progress.  If the fetch fails, an error notification is shown and the calendar renders without events.

Note that the visible range is driven by the calendar itself in this mode (use \`initialDate\`/\`visibleRange\` to control it), rather than being derived from the events.`
        },
        {
            name: "slotLaneClass",
            type: "function",
            exactType: "(info: SlotLaneInfo) => string",
            template: `slotLaneClass={(info) => clsx(
    "fc-timeline-slot",
    info.date.getDate() === 1 && "fc-first-day-of-month",
    info.date.getDate() === new Date(info.date.getFullYear(), info.date.getMonth() + 1, 0).getDate() && "fc-last-day-of-month",
    $1
)}`,
            description: "Customize the CSS classes applied to slot lane elements. The default adds classes for first and last days of the month. Passes through [SlotLaneInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/render-hook-misc.ts#L4) which contains details about the slot lane."
        },
        {
            name: "eventClass",
            type: "function",
            exactType: "(event: EventDisplayInfo) => string",
            template: `eventClass={(event) => clsx(
    "fc-event",
    !event.options.eventOverlap && "fc-timeline-event",
    event.isStart && "fc-event-start",
    event.isEnd && "fc-event-end",
    event.isFuture && "fc-event-future",
    event.isPast && "fc-event-past",
    event.isToday && "fc-event-today",
    event.isSelected && "fc-event-selected",
    event.isDraggable && "fc-event-draggable",
    event.isDragging && "fc-event-dragging",
    event.isMirror && "fc-event-mirror",
    (event.isStartResizable || event.isEndResizable) && "fc-event-resizable",
    event.isResizing && "fc-event-resizing",
    $1
)}`,
            description: "Customize the CSS classes applied to event elements. Passes through [EventDisplayInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/component-util/event-rendering.ts#L232) which contains details about the event."
        },
        {
            name: "eventInnerClass",
            type: "function",
            exactType: "((event: EventDisplayInfo) => string)",
            template: `eventInnerClass={(event) => clsx(
    "fc-event-main",
    $1
)}`,
            description: "Customize the CSS classes applied to the inner event element. Passes through [EventDisplayInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/component-util/event-rendering.ts#L232) which contains details about the event."
        },
        {
            name: "eventTitleClass",
            type: "function",
            exactType: "((event: EventDisplayInfo) => string)",
            template: `eventTitleClass={(event) => clsx(
    "fc-event-title",
    $1
)}`,
            description: "Customize the CSS classes applied to the event title element. Passes through [EventDisplayInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/component-util/event-rendering.ts#L232) which contains details about the event."
        },
        {
            name: "eventTimeClass",
            type: "function",
            exactType: "((event: EventDisplayInfo) => string)",
            template: `eventTimeClass={(event) => clsx(
    "fc-event-time",
    $1
)}`,
            description: "Customize the CSS classes applied to the event time element. Passes through [EventDisplayInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/component-util/event-rendering.ts#L232) which contains details about the event."
        },
        {
            name: "rowEventClass",
            type: "function",
            exactType: "((event: EventDisplayInfo) => string)",
            template: `rowEventClass={(event) => clsx(
    "fc-h-event",
    $1
)}`,
            description: "Customize the CSS classes applied to row event elements. Passes through [EventDisplayInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/component-util/event-rendering.ts#L232) which contains details about the event."
        },
        {
            name: "rowEventInnerClass",
            type: "function",
            exactType: "(event: EventDisplayInfo) => string",
            template: `rowEventInnerClass={(event) => clsx(
    "fc-timeline-event",
    event.options.eventOverlap ? "fc-timeline-overlap-enabled" : "fc-timeline-overlap-disabled",
    $1
)}`,
            description: "Customize the CSS classes applied to inner row event elements. Passes through [EventDisplayInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/component-util/event-rendering.ts#L232) which contains details about the event."
        },
        {
            name: "moreLinkClass",
            type: "function",
            exactType: "((info: MoreLinkInfo) => string)",
            template: `moreLinkClass={(info) => clsx(
    "fc-more-link",
    $1
)}`,
            description: "Customize the CSS classes applied to 'more' link elements. Passes through [MoreLinkInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/common/MoreLinkContainer.tsx#L39) which contains details about the 'more' link."
        },
        {
            name: "moreLinkInnerClass",
            type: "function",
            exactType: "((info: MoreLinkInfo) => string)",
            template: `moreLinkInnerClass={(info) => clsx(
    "fc-more-link-inner",
    $1
)}`,
            description: "Customize the CSS classes applied to inner 'more' link elements. Passes through [MoreLinkInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/common/MoreLinkContainer.tsx#L39) which contains details about the 'more' link."
        },
        {
            name: "buttonClass",
            type: "function",
            exactType: "(button: ButtonInfo) => string",
            template: `buttonClass={(button) => clsx(
    "fc-button",
    "fc-button-primary",
    button.isSelected && "fc-button-selected",
    button.isDisabled && "fc-button-disabled",
    \`fc-\${button.name}-button\`,
    $1
)}`,
            description: "Customize the CSS classes applied to toolbar button elements. Passes through [ButtonInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/toolbar-struct.ts#L31) which contains details about the button."
        },
        {
            name: "headerToolbarClass",
            type: "function",
            exactType: "((info: ToolbarInfo) => string)",
            template: `headerToolbarClass={(info) => clsx(
    "fc-header-toolbar",
    $1
)}`,
            description: "Customize the CSS classes applied to the header toolbar. Passes through [ToolbarInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/toolbar-struct.ts#L13) which contains details about the toolbar."
        },
        {
            name: "toolbarClass",
            type: "function",
            exactType: "((info: ToolbarInfo) => string)",
            template: `toolbarClass={(info) => clsx(
    "fc-toolbar",
    $1
)}`,
            description: "Customize the CSS classes applied to toolbar elements. Passes through [ToolbarInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/toolbar-struct.ts#L13) which contains details about the toolbar."
        },
        {
            name: "toolbarTitleClass",
            type: "string",
            template: `toolbarTitleClass="fc-toolbar-title $1"`,
            description: "Customize the CSS classes applied to the toolbar title. Default: 'fc-toolbar-title'."
        },
        {
            name: "tableClass",
            type: "function",
            exactType: "((info: TableDisplayInfo) => string)",
            template: `tableClass={(info) => clsx(
    "fc-scrollgrid",
    $1
)}`,
            description: "Customize the CSS classes applied to table elements. Passes through [TableDisplayInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/common/TableAndSubsections.ts#L2) which contains details about the table."
        },
        {
            name: "tableBodyClass",
            type: "function",
            exactType: "((info: TableDisplayInfo) => string)",
            template: `tableBodyClass={(info) => clsx(
    "fc-scrollgrid-section-body",
    $1
)} `,
            description: "Customize the CSS classes applied to table body elements. Passes through [TableDisplayInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/common/TableAndSubsections.ts#L2) which contains details about the table."
        },
        {
            name: "dayHeaderClass",
            type: "function",
            exactType: "((info: DayHeaderInfo) => string)",
            template: `dayHeaderClass={(info) => clsx(
    "fc-col-header-cell",
    $1
)} `,
            description: "Customize the CSS classes applied to day header cells. Passes through [DayHeaderInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/render-hook-misc.ts#L23) which contains details about the day header."
        },
        {
            name: "resourceDayHeaderClass",
            type: "function",
            exactType: "((info: ResourceDayHeaderInfo) => string)",
            template: `resourceDayHeaderClass={(info) => clsx(
    "fc-col-header-cell",
    $1
)} `,
            description: "Customize the CSS classes applied to resource day header cells. Passes through [ResourceDayHeaderInfo](https://github.com/fullcalendar/fullcalendar-workspace/blob/main/premium/packages/preact-scheduler/src/resource-daygrid/structs.ts#L6) which contains details about the resource day header."
        },
        {
            name: "listDayHeaderClass",
            type: "function",
            exactType: "((info: ListDayHeaderInfo) => string)",
            template: `listDayHeaderClass={(info) => clsx(
    "fc-col-header-cell",
    $1
)} `,
            description: "Customize the CSS classes applied to list day header cells. Passes through [ListDayHeaderInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/list/structs.ts#L10) which contains details about the list day header."
        },
        {
            name: "slotHeaderClass",
            type: "function",
            exactType: "((info: SlotHeaderInfo) => string)",
            template: `slotHeaderClass={(info) => clsx(
    "fc-col-header-cell",
    $1
)} `,
            description: "Customize the CSS classes applied to slot header cells. Passes through [SlotHeaderInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/render-hook-misc.ts#L10) which contains details about the slot header."
        },
        {
            name: "slotHeaderRowClass",
            type: "string",
            template: `slotHeaderRowClass="fc-timeline-slots $1"`,
            description: "Customize the CSS classes applied to the slot header row. Default: 'fc-timeline-slots'."
        },
        {
            name: "tableHeaderClass",
            type: "function",
            exactType: "(info: TableHeaderInfo) => string",
            template: `tableHeaderClass={(info) => clsx(
    "fc-col-header-cell",
    "fc-scrollgrid-section-header",
    info.isSticky && "fc-scrollgrid-section-sticky",
    $1
)}`,
            description: "Customize the CSS classes applied to table header elements. Passes through [TableHeaderInfo](https://github.com/fullcalendar/fullcalendar/blob/main/packages/preact/src/common/TableAndSubsections.ts#L10) which contains details about the table header."
        },
        {
            name: "resourceColumnHeaderInnerClass",
            type: "function",
            exactType: "((info: ResourceColumnHeaderInfo) => string)",
            template: `resourceColumnHeaderInnerClass={(info) => clsx(
    "fc-datagrid-cell-cushion",
    $1
)} `,
            description: "Customize the CSS classes applied to resource column header inner elements. Passes through [ResourceColumnHeaderInfo](https://github.com/fullcalendar/fullcalendar-workspace/blob/main/premium/packages/preact-scheduler/src/resource-timeline/structs.ts#L7) which contains details about the resource column header."
        },
        {
            name: "resourceGroupHeaderInnerClass",
            type: "function",
            exactType: "((info: ResourceGroupHeaderInfo) => string)",
            template: `resourceGroupHeaderInnerClass={(info) => clsx(
    "fc-datagrid-cell-cushion",
    $1
)} `,
            description: "Customize the CSS classes applied to resource group header inner elements. Passes through [ResourceGroupHeaderInfo](https://github.com/fullcalendar/fullcalendar-workspace/blob/main/premium/packages/preact-scheduler/src/resource-timeline/structs.ts#L18) which contains details about the resource group header."
        },
        {
            name: "resourceCellClass",
            type: "function",
            exactType: "((info: ResourceCellInfo) => string)",
            template: `resourceCellClass={(info) => clsx(
    "fc-datagrid-cell",
    $1
)} `,
            description: "Customize the CSS classes applied to resource cell elements. Passes through [ResourceCellInfo](https://github.com/fullcalendar/fullcalendar-workspace/blob/main/premium/packages/preact-scheduler/src/resource-timeline/structs.ts#L11) which contains details about the resource cell."
        },
        {
            name: "resourceCellInnerClass",
            type: "function",
            exactType: "((info: ResourceCellInfo) => string)",
            template: `resourceCellInnerClass={(info) => clsx(
    "fc-datagrid-cell-cushion",
    $1
)} `,
            description: "Customize the CSS classes applied to resource cell inner elements. Passes through [ResourceCellInfo](https://github.com/fullcalendar/fullcalendar-workspace/blob/main/premium/packages/preact-scheduler/src/resource-timeline/structs.ts#L11) which contains details about the resource cell."
        },
        {
            name: "resourceExpanderClass",
            type: "function",
            exactType: "(info: ResourceExpanderInfo) => string",
            template: `resourceExpanderClass={(info) => clsx(
    "fc-datagrid-expander",
    info.isExpanded ? "fc-icon-minus-square" : "fc-icon-plus-square",
    $1
)}`,
            description: "Customize the CSS classes applied to resource expander elements. Passes through [ResourceExpanderInfo](https://github.com/fullcalendar/fullcalendar-workspace/blob/main/premium/packages/preact-scheduler/src/resource-timeline/structs.ts#L39) which contains details about the resource expander."
        },
        {
            name: "resourceLaneClass",
            type: "function",
            exactType: "((info: ResourceLaneInfo) => string)",
            template: `resourceLaneClass={(info) => clsx(
    "fc-timeline-lanes",
    $1
)} `,
            description: "Customize the CSS classes applied to resource lane elements. Passes through [ResourceLaneInfo](https://github.com/fullcalendar/fullcalendar-workspace/blob/main/premium/packages/preact-scheduler/src/resource-timeline/structs.ts#L34) which contains details about the resource lane."
        },
        {
            name: "resourceLaneTopClass",
            type: "function",
            exactType: "(info: ResourceLaneInfo) => string",
            template: `resourceLaneTopClass={(info) => clsx(
    "fc-timeline-events",
    info.options.eventOverlap ? "fc-timeline-overlap-enabled" : "fc-timeline-overlap-disabled",
    $1
)}`,
            description: "Customize the CSS classes applied to the top layer of resource lanes. Passes through [ResourceLaneInfo](https://github.com/fullcalendar/fullcalendar-workspace/blob/main/premium/packages/preact-scheduler/src/resource-timeline/structs.ts#L34) which contains details about the resource lane."
        },
        {
            name: "resourceLaneBottomClass",
            type: "function",
            exactType: "(info: ResourceLaneInfo) => string",
            template: `resourceLaneBottomClass={(info) => clsx(
    "fc-timeline-events",
    info.options.eventOverlap ? "fc-timeline-overlap-enabled" : "fc-timeline-overlap-disabled",
    $1
)}`,
            description: "Customize the CSS classes applied to the bottom layer of resource lanes. Passes through [ResourceLaneInfo](https://github.com/fullcalendar/fullcalendar-workspace/blob/main/premium/packages/preact-scheduler/src/resource-timeline/structs.ts#L34) which contains details about the resource lane."
        },
    ]
};

export { Calendar };